import React from 'react';

import { useIntl } from 'react-intl';

import { useBulkCreateSpace } from 'src/features/spaces/api/useBulkCreateSpace';
import { useCreateSpace } from 'src/features/spaces/api/useCreateSpace';
import { LIST_SPACES_RESOURCE } from 'src/features/spaces/api/useListSpaces';
import BulkSpaceFormModal from 'src/features/spaces/components/form/BulkSpaceFormModal';
import SpaceFormModal from 'src/features/spaces/components/form/SpaceFormModal';
import { Space } from 'src/features/spaces/utils/types';
import { spacesMessages } from 'src/features/spaces/utils/messages';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useParking } from 'src/providers/ParkingProvider';

interface SpaceFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedSpace?: Space;
    children?: React.ReactNode;
}

function CreateSpaceFormModal<TData>({ className, semantics }: SpaceFormModalProps<TData>) {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const { mutate, isPending } = useCreateSpace({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_SPACES_RESOURCE] });
            toast({
                title: formatMessage(spacesMessages.createSpaceSuccess),
                description: formatMessage(spacesMessages.createSpaceSuccessDescription),
            });
        },
    });

    const { mutate: mutateBulkSpace, isPending: isPendingBulkCreate } = useBulkCreateSpace({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_SPACES_RESOURCE] });
            toast({
                title: formatMessage(spacesMessages.createBulkSpaceSuccess),
                description: formatMessage(spacesMessages.createBulkSpaceSuccessDescription),
            });
        },
    });

    return (
        <div className="flex gap-2">
            <SpaceFormModal
                className={className}
                mutate={(data, onSuccess, onError) => mutate({ ...data, parkingId }, { onSuccess, onError })}
                title={formatMessage(generalMessages.createEntry, { entry: semantics?.entryName?.toLowerCase() })}
                icon={ICON_MAP.ADD}
                isPending={isPending}
                subtitle={formatMessage(spacesMessages.addSpaceSubtitle)}
            />
            <BulkSpaceFormModal
                className={className}
                mutate={(data, onSuccess, onError) => mutateBulkSpace({ ...data, parkingId }, { onSuccess, onError })}
                title={formatMessage(generalMessages.createEntry, {
                    entry: formatMessage(spacesMessages.multipleSpaces).toLowerCase(),
                })}
                icon={ICON_MAP.ADD}
                isPending={isPendingBulkCreate}
                subtitle={formatMessage(spacesMessages.addMultipleSpacesSubtitle)}
            />
        </div>
    );
}

export default CreateSpaceFormModal;
